import type { SigningPayload } from "@breakout/sdk"
import { PERMIT2_ADDRESS, permitAbi } from "@breakout/sdk"
import type { Address, PublicClient } from "viem"

// Re-export PERMIT2_ADDRESS for use by components that import from here
export { PERMIT2_ADDRESS }

export const USDC_PERMIT_VERSION = "2"
export const USDC_TOKEN_NAME = "USD Coin"
export const DEFAULT_DEADLINE_SECONDS = 600
export const PERMIT_TYPES = {
	Permit: [
		{ name: "owner", type: "address" },
		{ name: "spender", type: "address" },
		{ name: "value", type: "uint256" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
} as const

interface GetPermitNonceParams {
	client: PublicClient
	usdc: Address
	owner: Address
}

export async function getPermitNonce({
	client,
	usdc,
	owner,
}: GetPermitNonceParams): Promise<bigint> {
	return client.readContract({
		address: usdc,
		abi: permitAbi,
		functionName: "nonces",
		args: [owner],
	})
}

interface BuildPermitSigningPayloadParams {
	chainId: number
	tokenAddress: Address
	tokenName: string
	owner: Address
	spender: Address
	value: bigint
	nonce: bigint
	deadline: bigint
}

export function buildPermitSigningPayload({
	chainId,
	tokenAddress,
	tokenName,
	owner,
	spender,
	value,
	nonce,
	deadline,
}: BuildPermitSigningPayloadParams): SigningPayload {
	return {
		domain: {
			name: tokenName,
			version: USDC_PERMIT_VERSION,
			chainId,
			verifyingContract: tokenAddress,
		},
		types: PERMIT_TYPES,
		primaryType: "Permit",
		message: {
			owner,
			spender,
			value,
			nonce,
			deadline,
		},
	}
}

/** Split an EIP-2612 permit signature into v, r, s components */
export function splitPermitSignature(
	signature: `0x${string}`,
): [number, `0x${string}`, `0x${string}`] {
	const sig = signature.slice(2)
	// Template literal types - we know these are valid hex strings since signature is validated
	const r: `0x${string}` = `0x${sig.slice(0, 64)}`
	const s: `0x${string}` = `0x${sig.slice(64, 128)}`
	const v = Number.parseInt(sig.slice(128, 130), 16)
	return [v, r, s]
}

/** Serialize SigningPayload for backend transmission (converts readonly to mutable) */
function serializeSigningPayload(payload: SigningPayload) {
	// Deep clone types to convert readonly arrays to mutable
	const mutableTypes: Record<string, { type: string; name: string }[]> = {}
	for (const [key, value] of Object.entries(payload.types)) {
		mutableTypes[key] = value.map(t => ({ type: t.type, name: t.name }))
	}

	return {
		domain: payload.domain,
		types: mutableTypes,
		primaryType: payload.primaryType,
		message: payload.message,
	}
}

export function serializeBuyPreparation(prep: {
	type: "buy"
	signingPayload: SigningPayload
	quote: {
		usdcIn: number
		sharesOut: number
		cost: number
		fee: number
		total: number
		avgPrice: number
		priceImpact: number
		priceBefore: number
		priceAfter: number
	}
	expiresAt: Date
	needsPermit2Approval: boolean
	_execution: {
		market: Address
		user: Address
		bucket: number
		usdcAmount: bigint
		minShares: bigint
		deadline: bigint
		usdcAddress: Address
		permit2Nonce: bigint
	}
}) {
	return {
		type: prep.type,
		signingPayload: serializeSigningPayload(prep.signingPayload),
		quote: prep.quote,
		expiresAt: prep.expiresAt.toISOString(),
		needsPermit2Approval: prep.needsPermit2Approval,
		_execution: {
			market: prep._execution.market,
			user: prep._execution.user,
			bucket: prep._execution.bucket,
			usdcAmount: prep._execution.usdcAmount.toString(),
			minShares: prep._execution.minShares.toString(),
			deadline: prep._execution.deadline.toString(),
			usdcAddress: prep._execution.usdcAddress,
			permit2Nonce: prep._execution.permit2Nonce.toString(),
		},
	}
}

export function serializeSellPreparation(prep: {
	type: "sell"
	signingPayload: SigningPayload
	quote: {
		sharesIn: number
		usdcOut: number
		proceeds: number
		fee: number
		avgPrice: number
		priceImpact: number
		priceBefore: number
		priceAfter: number
	}
	expiresAt: Date
	_execution: {
		market: Address
		user: Address
		bucket: number
		shares: bigint
		minProceeds: bigint
		nonce: bigint
		deadline: bigint
	}
}) {
	return {
		type: prep.type,
		signingPayload: serializeSigningPayload(prep.signingPayload),
		quote: prep.quote,
		expiresAt: prep.expiresAt.toISOString(),
		_execution: {
			market: prep._execution.market,
			user: prep._execution.user,
			bucket: prep._execution.bucket,
			shares: prep._execution.shares.toString(),
			minProceeds: prep._execution.minProceeds.toString(),
			nonce: prep._execution.nonce.toString(),
			deadline: prep._execution.deadline.toString(),
		},
	}
}

export function serializeClaimPreparation(prep: {
	type: "claim"
	signingPayload: SigningPayload
	claimable: number
	expiresAt: Date
	_execution: {
		market: Address
		user: Address
		nonce: bigint
		deadline: bigint
	}
}) {
	return {
		type: prep.type,
		signingPayload: serializeSigningPayload(prep.signingPayload),
		claimable: prep.claimable,
		expiresAt: prep.expiresAt.toISOString(),
		_execution: {
			market: prep._execution.market,
			user: prep._execution.user,
			nonce: prep._execution.nonce.toString(),
			deadline: prep._execution.deadline.toString(),
		},
	}
}
